import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MenuListProps } from 'react-select';
import { Poppins } from 'src/common';
import { ChevronUp, ChevronDown } from 'src/common/Chevron';
import styled from 'styled-components';
import { FrameworkControlOption } from '../types';

const GridContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  padding: 8px;
  padding-bottom: 0;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  box-shadow: 0 2px 8px ${({ theme }) => theme.colors.shadow};
  width: 100%;
  max-width: auto;
  min-width: 0;
  transform: translateY(10px);
`;

const GridHeader = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 0.9fr 2fr 150px;
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
  min-width: 0;
`;

const GridHeaderCell = styled.div`
  padding: 16px 12px;
  color: ${({ theme }) => theme.colors.prussianBlue};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const SortIcon = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 6px;
  flex-shrink: 0;
`;

const GridBodyContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 0.9fr 2fr 150px;
  gap: 0;
  min-width: 0;
`;

const GridRow = styled.div`
  display: contents;

  &:hover > div {
    background-color: ${({ theme }) => theme.colors.cultured};
    cursor: pointer;
  }
`;

const GridCell = styled.div`
  padding: 8px 12px;
  color: ${({ theme }) => theme.colors.davysGrey};
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
`;

type SortColumn = 'framework' | 'control_identifier' | 'label' | 'author';
type SortDirection = 'asc' | 'desc';

export const MenuList = <OptionType extends FrameworkControlOption, IsMulti extends boolean>(
  props: MenuListProps<OptionType, IsMulti>,
) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>('framework');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const options = React.Children.toArray(props.children)
    .map((child: any) => {
      return child?.props?.data as FrameworkControlOption;
    })
    .filter(Boolean);

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => {
      const aValue = (a[sortColumn] || '')?.toLowerCase();
      const bValue = (b[sortColumn] || '')?.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [options, sortColumn, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  return (
    <GridContainer>
      <GridHeader>
        <GridHeaderCell onClick={() => handleSort('framework')}>
          <Poppins px={18} weight={600}>
            Framework
          </Poppins>
          <SortIcon>
            <ChevronUp active={sortColumn === 'framework' && sortDirection === 'asc'} />
            <ChevronDown active={sortColumn === 'framework' && sortDirection === 'desc'} />
          </SortIcon>
        </GridHeaderCell>
        <GridHeaderCell onClick={() => handleSort('control_identifier')}>
          <Poppins px={18} weight={600} nowrap>
            Control ID
          </Poppins>
          <SortIcon>
            <ChevronUp active={sortColumn === 'control_identifier' && sortDirection === 'asc'} />
            <ChevronDown active={sortColumn === 'control_identifier' && sortDirection === 'desc'} />
          </SortIcon>
        </GridHeaderCell>
        <GridHeaderCell onClick={() => handleSort('label')}>
          <Poppins px={18} weight={600}>
            Name
          </Poppins>
          <SortIcon>
            <ChevronUp active={sortColumn === 'label' && sortDirection === 'asc'} />
            <ChevronDown active={sortColumn === 'label' && sortDirection === 'desc'} />
          </SortIcon>
        </GridHeaderCell>
        <GridHeaderCell onClick={() => handleSort('author')}>
          <Poppins px={18} weight={600}>
            Author
          </Poppins>
          <SortIcon>
            <ChevronUp active={sortColumn === 'author' && sortDirection === 'asc'} />
            <ChevronDown active={sortColumn === 'author' && sortDirection === 'desc'} />
          </SortIcon>
        </GridHeaderCell>
      </GridHeader>
      <GridBodyContainer>
        <GridWrapper>
          {sortedOptions.map((el) => (
            <GridRow
              key={el.value}
              onClick={() => {
                const childArray = React.Children.toArray(props.children);
                const child = childArray.find((child: any) => child?.props?.data?.value === el.value) as any;
                if (child?.props?.innerProps?.onClick) {
                  child.props.innerProps.onClick();
                }
              }}
            >
              <GridCell data-cy={`framework-control-select-menu-item`}>
                <Poppins px={14}>{el.framework}</Poppins>
              </GridCell>
              <GridCell>
                <Poppins px={14}>{el.control_identifier}</Poppins>
              </GridCell>
              <GridCell>
                <Link
                  to={`/control-frameworks/all-controls/${el.value}`}
                  onClick={(e) => e.stopPropagation()}
                  target="_blank"
                >
                  <Poppins px={14} nowrap className="table-link">
                    {el.label}
                  </Poppins>
                </Link>
              </GridCell>
              <GridCell>
                <Poppins px={14}>{el.author}</Poppins>
              </GridCell>
            </GridRow>
          ))}
        </GridWrapper>
      </GridBodyContainer>
    </GridContainer>
  );
};
